// Bridge between a patch's sample references and Strudel's URL-loaded
// sample registry. Given a list of sample names that aren't currently
// known to Strudel, query Supabase for matching rows the user can see
// (RLS gates ownership + visibility), then register them via
// Strudel's `samples({ name: url })` so subsequent `s("name")` calls
// resolve to a lazy URL loader instead of throwing per-cycle.
//
// Best-effort: failures are swallowed by the caller. The next play
// attempt will retry, and any names we couldn't resolve still surface
// the original "sample not registered" error — which is the right
// behaviour for genuinely unknown names (typos, deleted samples).
//
// Why not pull-to-local: lazy URL loading is enough for playback. If
// the user wants the sample as a permanent local kit member, the
// Mine → Cloud → Pull button is the deliberate action. This resolver
// is the implicit one that just makes Play work.

import { getSupabase } from '../auth/client';
import { samples as registerSamples } from '../strudel/extensions';
import { cloudSampleUrl } from './playback';

type ResolvedRow = {
  name: string;
  r2_key: string;
};

/** Look up cloud samples matching `names`, register their public R2
 *  URLs with Strudel, and return the names actually resolved. Names
 *  that no row matched are left untouched — those `s("…")` calls
 *  will still fail at trigger time, which is the honest behaviour
 *  for typos. */
export async function resolvePatchSamples(
  names: string[],
): Promise<string[]> {
  if (names.length === 0) return [];
  const supabase = getSupabase();
  if (!supabase) return [];

  // RLS gates this: anonymous + signed-in users see public samples;
  // signed-in users additionally see their own private rows. The
  // `patches!inner` join is required because RLS on `samples`
  // delegates to the parent patch's visibility/ownership.
  const { data, error } = await supabase
    .from('samples')
    .select('name, r2_key, patches!inner(visibility)')
    .in('name', names);
  if (error) {
    console.warn('[resolvePatchSamples] supabase query failed', {
      names,
      error,
    });
    return [];
  }

  const rows = (data ?? []) as ResolvedRow[];
  // Multiple rows can share a name across different users (e.g.
  // someone forks a kit and re-publishes). Pick the first match per
  // name — RLS already constrained the result set to ones the user
  // is allowed to see, and any of them resolves to working audio.
  const map: Record<string, string> = {};
  for (const row of rows) {
    if (map[row.name] !== undefined) continue;
    const url = cloudSampleUrl(row.r2_key);
    if (url) map[row.name] = url;
  }
  const resolved = Object.keys(map);
  if (resolved.length === 0) return [];

  try {
    await registerSamples(map);
  } catch {
    // Strudel's samples() is async and best-effort; if registration
    // throws, the names won't actually be playable. Drop them from
    // the resolved list so callers can decide whether to retry.
    return [];
  }
  return resolved;
}
